import React, { useEffect } from 'react';
import { Form, InputNumber, Modal, message } from '@maxtropy/components';
import { GAS_INDICATORS, WATER_INDICATORS, DeviceCategory, DeviceThresholds, DEVICE_CATEGORY } from '../types';

interface ThresholdConfigModalProps {
  open: boolean;
  isEdit: boolean;
  deviceType: DeviceCategory;
  deviceName: string;
  currentThresholds?: DeviceThresholds;
  onOk: (thresholds: DeviceThresholds) => void;
  onCancel: () => void;
}

const ThresholdConfigModal: React.FC<ThresholdConfigModalProps> = ({
  open,
  isEdit,
  deviceType,
  deviceName,
  currentThresholds,
  onOk,
  onCancel,
}) => {
  const MAX_THRESHOLD_VALUE = 99999999.99;
  const LABEL_WIDTH = 128;
  const INPUT_HEIGHT = 32;
  const PH_INPUT_WIDTH = 100;
  const [form] = Form.useForm();

  const indicators = deviceType === DEVICE_CATEGORY.Gas ? GAS_INDICATORS : WATER_INDICATORS;
  const isPhIndicator = (key: string) => key === 'phLowerLimit' || key === 'phUpperLimit';
  const getIndicatorMax = (key: string) => (isPhIndicator(key) ? 14 : MAX_THRESHOLD_VALUE);

  useEffect(() => {
    if (open) {
      const initialValues: Record<string, number | undefined> = {};
      indicators.forEach(indicator => {
        const currentValue = currentThresholds?.[indicator.key as keyof DeviceThresholds];
        initialValues[indicator.key] = isEdit ? currentValue ?? undefined : currentValue ?? indicator.defaultValue;
      });
      form.resetFields();
      form.setFieldsValue(initialValues);
    }
  }, [open, isEdit, currentThresholds, indicators, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const lower = values.phLowerLimit;
      const upper = values.phUpperLimit;
      if (lower !== undefined && upper !== undefined && lower >= upper) {
        message.warning('PH值下限需小于上限');
        return;
      }
      onOk(values);
    } catch {
      // 校验失败
    }
  };

  const renderIndicatorRows = () => {
    const phLowerIndicator = indicators.find(indicator => indicator.key === 'phLowerLimit');
    const phUpperIndicator = indicators.find(indicator => indicator.key === 'phUpperLimit');
    const commonIndicators = indicators.filter(indicator => !isPhIndicator(indicator.key));

    return (
      <div>
        {commonIndicators.map(indicator => (
          <div
            key={indicator.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 24,
              lineHeight: `${INPUT_HEIGHT}px`,
            }}
          >
            <div style={{ width: LABEL_WIDTH }}>{`${indicator.label}：`}</div>
            <div style={{ marginRight: 8 }}>每小时&lt;=</div>
            <Form.Item key={indicator.key} name={indicator.key} style={{ marginBottom: 0, flex: 1 }}>
              <InputNumber
                placeholder="请输入阈值"
                min={0}
                max={getIndicatorMax(indicator.key)}
                precision={2}
                style={{ width: '100%' }}
                suffix={indicator.unit}
              />
            </Form.Item>
          </div>
        ))}

        {phLowerIndicator && phUpperIndicator ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 24,
              lineHeight: `${INPUT_HEIGHT}px`,
            }}
          >
            <div style={{ width: LABEL_WIDTH }}>PH值：</div>
            <Form.Item name="phLowerLimit" style={{ marginBottom: 0 }}>
              <InputNumber min={0} max={14} precision={2} style={{ width: PH_INPUT_WIDTH }} />
            </Form.Item>
            <div style={{ margin: '0 8px' }}>~</div>
            <Form.Item name="phUpperLimit" style={{ marginBottom: 0 }}>
              <InputNumber min={0} max={14} precision={2} style={{ width: PH_INPUT_WIDTH }} />
            </Form.Item>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <Modal
      title={`${deviceName} - 指标阈值配置`}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      destroyOnClose
      contentClassName="modal-form-content"
    >
      <Form form={form}>{renderIndicatorRows()}</Form>
    </Modal>
  );
};

export default ThresholdConfigModal;
